import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { clientFetch } from "../utils/clientFetch";
import { notifyHttpError } from "../utils/apiErrorHandler";

/**
 * 契約プラン管理 (#467 / 管理画面 Phase A) のデータ取得・操作フック。
 *
 * apiKeyContext.tsx を雛形として踏襲。契約プラン一覧画面と企業プラン割当画面の双方で使うため、
 * listPlans (ドロップダウン用) を含めて 1 つの Context で提供する。
 *
 * 重要 (受入条件): updatePlan は full-replace。呼び出し元は code 以外の全フィールドを必ず送ること
 * (max 系 / allow_ip_restriction を省略するとサーバが既定値に静かにダウングレードする)。
 */

const API_BASE = `${process.env.REACT_APP_API_ENDPOINT}/apis/admin`;

/** 契約プラン (一覧/作成/更新レスポンス)。 */
export type ContractPlan = {
  id: number;
  code: string;
  name: string;
  description: string | null;
  rate_limit_per_minute: number;
  rate_limit_per_hour: number;
  rate_limit_image_per_minute: number;
  max_page_size: number;
  max_api_keys_per_company: number;
  max_redeliveries_per_day: number;
  allow_ip_restriction: boolean;
  is_active: boolean;
};

/**
 * プラン作成/更新の入力。
 * 作成 (POST) では max 系 / allow_ip_restriction を省略するとサーバが既定補完するが、
 * 更新 (PUT) は full-replace のため呼び出し元が code 以外の全フィールドを必ず埋めること。
 */
export type PlanInput = {
  code: string;
  name: string;
  description: string | null;
  rate_limit_per_minute: number;
  rate_limit_per_hour: number;
  rate_limit_image_per_minute: number;
  max_page_size: number;
  max_api_keys_per_company: number;
  max_redeliveries_per_day: number;
  allow_ip_restriction: boolean;
};

/** 企業へのプラン割当入力。override は null=プラン値使用。 */
export type AssignInput = {
  contract_plan_id: number;
  rate_limit_per_minute_override: number | null;
  rate_limit_per_hour_override: number | null;
  rate_limit_image_per_minute_override: number | null;
  max_page_size_override: number | null;
  max_api_keys_per_company_override: number | null;
  max_redeliveries_per_day_override: number | null;
};

/** マージ後の実効上限。キーはサーバの返却内容をそのまま表示する (確実なキー: maxApiKeysPerCompany, allowIpRestriction)。 */
export type EffectiveLimits = {
  effective_limits: Record<string, unknown>;
  plan_code: string | null;
};

/** 各操作の結果 (成功 data or エラーメッセージ)。 */
export type PlanResult<T> = { ok: true; data: T } | { ok: false; message: string };

async function parseError(res: Response): Promise<string> {
  try {
    const body = await res.json();
    return body?.message || `エラーが発生しました (${res.status})`;
  } catch {
    return `エラーが発生しました (${res.status})`;
  }
}

/**
 * 割当フォームのオーバーライド入力を API ペイロードへ正規化する純関数 (テスト対象)。
 * 空欄/未入力 (null/undefined/"") は null (= プラン値使用) にする。**0 にしてはいけない**
 * (空欄を 0 にすると顧客のレート上限を 0 に絞ってしまう)。
 */
export function toAssignPayload(
  contractPlanId: number,
  overrides: Partial<Record<keyof Omit<AssignInput, "contract_plan_id">, number | null | undefined | "">>
): AssignInput {
  const norm = (v: number | null | undefined | ""): number | null =>
    v === null || v === undefined || v === "" ? null : Number(v);
  return {
    contract_plan_id: contractPlanId,
    rate_limit_per_minute_override: norm(overrides.rate_limit_per_minute_override),
    rate_limit_per_hour_override: norm(overrides.rate_limit_per_hour_override),
    rate_limit_image_per_minute_override: norm(overrides.rate_limit_image_per_minute_override),
    max_page_size_override: norm(overrides.max_page_size_override),
    max_api_keys_per_company_override: norm(overrides.max_api_keys_per_company_override),
    max_redeliveries_per_day_override: norm(overrides.max_redeliveries_per_day_override),
  };
}

export function useContractPlanContext() {
  const [plans, setPlans] = useState<ContractPlan[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  /** プラン一覧取得 (active/inactive 両方)。 */
  const listPlans = useCallback(async () => {
    setLoading(true);
    try {
      const res = await clientFetch(`${API_BASE}/contract-plans`, { method: "GET" });
      if (!res.ok) {
        notifyHttpError(res, { logMessage: "Error fetching contract plans." });
        setPlans([]);
        return;
      }
      const body = await res.json();
      setPlans(Array.isArray(body?.data) ? body.data : []);
    } finally {
      setLoading(false);
    }
  }, []);

  /** プラン作成。成功で作成プランを返す。 */
  const createPlan = useCallback(async (input: PlanInput): Promise<PlanResult<ContractPlan>> => {
    const res = await clientFetch(`${API_BASE}/contract-plans`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    if (!res.ok) return { ok: false, message: await parseError(res) };
    const body = await res.json();
    return { ok: true, data: body.data as ContractPlan };
  }, []);

  /**
   * プラン更新 (full-replace)。input は code 以外の全フィールドを含むこと。
   * (max 系 / allow_ip_restriction を省略すると既定値へ静かにダウングレードされる)
   */
  const updatePlan = useCallback(async (id: number, input: PlanInput): Promise<PlanResult<ContractPlan>> => {
    const res = await clientFetch(`${API_BASE}/contract-plans/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    if (!res.ok) return { ok: false, message: await parseError(res) };
    const body = await res.json();
    return { ok: true, data: body.data as ContractPlan };
  }, []);

  /** プラン無効化。新規割当不可になるが既存企業は影響なし。 */
  const deactivatePlan = useCallback(async (id: number): Promise<PlanResult<{ id: number; is_active: boolean }>> => {
    const res = await clientFetch(`${API_BASE}/contract-plans/${id}/deactivate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!res.ok) return { ok: false, message: await parseError(res) };
    const body = await res.json();
    return { ok: true, data: body.data as { id: number; is_active: boolean } };
  }, []);

  /** 企業へプランを割当 (オーバーライド込み)。成功で割当結果を返す。 */
  const assignToCompany = useCallback(
    async (companyId: number, input: AssignInput): Promise<PlanResult<Record<string, unknown>>> => {
      const res = await clientFetch(`${API_BASE}/companies/${companyId}/contract-plan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) return { ok: false, message: await parseError(res) };
      const body = await res.json();
      return { ok: true, data: (body.data ?? {}) as Record<string, unknown> };
    },
    []
  );

  /** 企業の実効上限 (マージ結果) と割当プラン code を取得する。 */
  const getEffectiveLimits = useCallback(async (companyId: number): Promise<EffectiveLimits | null> => {
    const res = await clientFetch(`${API_BASE}/companies/${companyId}/effective-limits`, { method: "GET" });
    if (!res.ok) return null;
    const body = await res.json();
    const data = body?.data ?? {};
    return {
      effective_limits: (data.effective_limits ?? {}) as Record<string, unknown>,
      plan_code: data.plan_code ?? null,
    };
  }, []);

  return useMemo(
    () => ({ plans, loading, listPlans, createPlan, updatePlan, deactivatePlan, assignToCompany, getEffectiveLimits }),
    [plans, loading, listPlans, createPlan, updatePlan, deactivatePlan, assignToCompany, getEffectiveLimits]
  );
}

type ContractPlanContextValue = ReturnType<typeof useContractPlanContext>;
const ContractPlanContext = createContext<ContractPlanContextValue | null>(null);

export function ContractPlanProvider({ children }: { children: React.ReactNode }) {
  const value = useContractPlanContext();
  return <ContractPlanContext.Provider value={value}>{children}</ContractPlanContext.Provider>;
}

export function useContractPlans(): ContractPlanContextValue {
  const ctx = useContext(ContractPlanContext);
  if (!ctx) throw new Error("useContractPlans must be used within ContractPlanProvider");
  return ctx;
}
